import { ChangeEvent, FC, useEffect, useState } from "react";
import Modal from "react-modal";
import { IProps } from "./types";
import styles from "./styles.module.scss";
import { CrossIcon, TrashIcon } from "../../assets";
import {
  Button,
  EditableUnit,
  IngredientImage,
  Input,
  Selector,
} from "../../components";
import {
  COLORS,
  defaultIngredientsUnit,
  IngredientCategory,
  ingredientsCategories,
} from "../../constants";
import { toast } from "react-hot-toast";
import { IUnit } from "../../models";
import GoogleImagesSearch from "../GoogleImagesSearch";
import { uploadFileToS3 } from "../../services/file.service";
import { createIngredient } from "../../services/ingredients.service";

const CreateIngredient: FC<IProps> = ({
  isOpen,
  setIsOpen,
  onGetIngredients,
}): JSX.Element => {
  const [name, setName] = useState("");

  const [category, setCategory] = useState<string>(ingredientsCategories[0]);
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);
  const [calories, setCalories] = useState<number>(0);
  const [proteins, setProteins] = useState<number>(0);
  const [fats, setFats] = useState<number>(0);
  const [carbs, setCarbs] = useState<number>(0);
  const [fiber, setFiber] = useState<number>(0);
  const [alcohol, setAlcohol] = useState<number>(0);

  const [defaultUnits, setDefaultUnits] = useState<IUnit[]>(
    defaultIngredientsUnit.map((item) => ({ ...item }))
  );
  const [addUnitName, setAddUnitName] = useState("");

  const [nameError, setNameError] = useState<string>("");
  const [servingSizeForOne, setServingSizeForOne] = useState<number>(0);
  const [isShowSearchWebImages, setIsShowSearchWebImages] = useState(false);

  const [imagePath, setImagePath] = useState("");
  const [imageFile, setImageFile] = useState<any>("");
  const [isImageUploadedFromWeb, setIsImageUploadedFromWeb] = useState(false);

  const onSelectFile = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }
      const type = e.target.files[0].type.slice(0, 5);

      if (type !== "image") {
        toast.error("Only pictures can be uploaded here");
        return;
      }
      setImagePath(URL.createObjectURL(e.target.files[0]));
      let file = e.target.files[0];
      setImageFile(e.target.files[0]);
      setIsImageUploadedFromWeb(false);
    } catch (error) {
      console.log(error);
    }
  };

  const onDeleteImage = () => {
    setImageFile("");
    setImagePath("");
  };

  const onChangeName = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };
  const [brand, setBrand] = useState("");

  const onChangeBrand = (e: ChangeEvent<HTMLInputElement>) => {
    setBrand(e.target.value);
  };

  const onCreateIngredient = async () => {
    try {
      setIsLoadingCreate(true);
      let imageLink = imagePath;
      if (imageFile) {
        if (isImageUploadedFromWeb) {
          const res = await uploadFileToS3({
            type: "link",
            path: "ingredients",
            link: imageLink,
          });
          imageLink = res?.data?.link;
        } else {
          let blob = new Blob([imageFile], { type: imageFile.type });

          const res = await uploadFileToS3({
            type: "file",
            path: "ingredients",
            file: blob,
          });
          imageLink = res?.data?.link;
        }
      }

      const { data } = await createIngredient(
        name,
        category,
        defaultUnits,
        brand,
        imageLink
      );
      if (data?.success) {
        toast.success("The ingredient was created successfully");
        setName("");
        setCalories(0);
        setProteins(0);
        setFats(0);
        setFiber(0);
        setCarbs(0);
        setAlcohol(0);
        setIsOpen(false);
        onGetIngredients();
        setNameError("");
        setServingSizeForOne(0);
        setServingSizeForOne(0);
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      toast.error(error?.message?.[0]?.message || "Something went wrong");
    } finally {
      setIsLoadingCreate(false);
    }
  };

  useEffect(() => {
    setName("");
    setCalories(0);
    setProteins(0);
    setFats(0);
    setFiber(0);
    setCarbs(0);
    setAlcohol(0);
    setNameError("");
    setCategory(ingredientsCategories[0]);
    setServingSizeForOne(0);
    setDefaultUnits(defaultIngredientsUnit);
    setBrand("");
  }, [isOpen]);

  const checkIsUnitsValid = () => {
    return defaultUnits.some((el) => {
      if (el.status && el.isDefault) {
        return true;
      } else {
        return false;
      }
    });
  };
  const isDisabled =
    name.length < 2 ||
    !category ||
    calories === undefined ||
    proteins === undefined ||
    fats === undefined ||
    carbs === undefined ||
    fiber === undefined ||
    alcohol === undefined ||
    servingSizeForOne === undefined ||
    !checkIsUnitsValid();

  const onAddUnit = (name: string) => {
    if (!addUnitName) {
      return;
    }
    setDefaultUnits((prev) => [
      ...prev,
      {
        name: name,
        grams: 0,
        description: "",
        isDefault: false,
        status: false,
        calories: 0,
        proteins: 0,
        fats: 0,
        carbs: 0,
        fiber: 0,
        alco: 0,
      },
    ]);
    setAddUnitName("");
  };

  const onChangeAddUnitName = (e: ChangeEvent<HTMLInputElement>) => {
    setAddUnitName(e.target.value);
  };
  return (
    <Modal
      isOpen={isOpen}
      shouldFocusAfterRender={false}
      onRequestClose={() => setIsOpen(false)}
      overlayClassName={styles.overlay}
      className={styles.modal}
      ariaHideApp={false}
    >
      <GoogleImagesSearch
        isOpen={isShowSearchWebImages}
        setIsOpen={setIsShowSearchWebImages}
        setImagePath={setImagePath}
        setImageFile={(file) => {
          setImageFile(file);
          setIsImageUploadedFromWeb(true);
        }}
      />
      <div className={styles.innerContainer}>
        <h6>Create New Ingredient</h6>
        <p className={styles.sectionTitle}>Ingredient image</p>
        <div className={styles.imageContainer}>
          <IngredientImage
            category={category as IngredientCategory}
            image={imagePath}
          />
          <label htmlFor={"photoInput"} className={styles.uploadImageButton}>
            Upload New
            <input
              id={"photoInput"}
              type={"file"}
              accept="image/*"
              onChange={onSelectFile}
              //@ts-ignore
              onClick={(e) => (e.target.value = null)}
              className={styles.uploadImageInput}
            />
          </label>
          <Button
            onClick={onDeleteImage}
            title="Delete"
            size="small"
            styleType="outlined"
            leftIcon={<TrashIcon width={24} height={24} />}
            customStyles={styles.deleteButton}
            textColor={COLORS.red}
          />
          <Button
            onClick={() => setIsShowSearchWebImages(true)}
            title="Upload from web"
            size="small"
            styleType="disabled"
            customStyles={styles.uploadWebButton}
          />
        </div>
        <div onClick={() => setIsOpen(false)} className={styles.closeButton}>
          <CrossIcon />
        </div>
        <div className={styles.inputContainer}>
          <Input
            value={name}
            onChange={onChangeName}
            label="Ingredient Name"
            placeholder="Ingredient Name"
            error={nameError}
          />
          <Input
            value={brand}
            onChange={onChangeBrand}
            label="Brand"
            placeholder="Brand"
          />

          <Selector
            value={category}
            setValue={(value) => {
              // setSelectedUnits([]);
              setCategory(value);
            }}
            data={ingredientsCategories}
            label="Category"
          />
        </div>
        <p className={styles.sectionTitle}>
          The amount of nutrients must be indicated per 1 unit.
        </p>

        {defaultUnits.map((item, index) => (
          <div key={index} className={styles.editableUnitWrapper}>
            {
              <EditableUnit
                setIngredientUnits={setDefaultUnits}
                index={index}
                item={item}
              />
            }
            <div className={styles.editableUnitDivider} />
          </div>
        ))}

        <Input
          placeholder="Unit Name"
          value={addUnitName}
          onChange={onChangeAddUnitName}
          wrapperStyles={styles.addUnitTitle}
        />

        <Button
          onClick={() => {
            onAddUnit(addUnitName);
          }}
          title={"Add Unit"}
          size="x-small"
          styleType="outlined"
          customStyles={styles.addUnitButton}
        />
        <Button
          title="Create"
          size="medium"
          styleType={isDisabled ? "disabled" : "filled"}
          loading={isLoadingCreate}
          onClick={onCreateIngredient}
          disabled={isDisabled}
        />
      </div>
    </Modal>
  );
};

export default CreateIngredient;
